/**
 * Product detail — `screens.md` §4, and the screen F3's ruling names by name.
 *
 * ## The header's third action arrived at c92, and the absence it replaces was the point
 *
 * §4's persistent header lists *Preview a scan · View live page · Download print files*. The
 * third was **not rendered at all** until c92, deliberately: a disabled control naming an
 * unbuilt feature is a remedy that does not exist — c29's rule — and it is worse than
 * absence in a specific way, because a greyed *Download print files* tells a brand the
 * feature is theirs and withheld. Now the screen behind it exists, so the action is a link
 * to it rather than a button that would have to hold the whole generator's state.
 *
 * *Preview a scan* is c35's simulator and opens the Links tab; **View live page** is real,
 * and opens exactly what a scan of the barcode gets — the Digital Link URI on the resolver
 * edge, composed from `digitalLinkPath` and the origin **the API hands over as data**
 * (`orgs.current`'s `resolverBaseUrl`, c34's mechanism). It is honest precisely because it
 * is the same URI, and the origin is not a literal in this component for the reason c21
 * bought at some expense: a service that reads its own scheme off the connection anchors
 * production linksets on `http://`.
 *
 * ## Six tabs, and the sixth is a screen rather than a tab
 *
 * §4's tab strip is *Details · Variants · Batches · Media · Links · Artwork*. **Links landed
 * in c35** and carries the simulator with it. **Artwork is c92's**, and it is a route rather
 * than a tab: the generator holds settings, a preflight, two confirmations and a download,
 * and a tab whose state is thrown away every time somebody looks at the batch list is a tab
 * that loses a print run's worth of choices. The header action is how it is reached, which
 * is what §4's own layout puts there.
 *
 * ## What this screen refuses to render (F7)
 *
 * The retired product's *"still reports its scan count"* — rollups are Phase 7 and §4
 * forbids the dashboard reading `scan_events` — and the gated attribute's visible-but-locked
 * state, because nothing is gated until Phase 5. Both would mean inventing data.
 */
import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";

import { Button, Input, Pill, Surface } from "@packagepage/ui";
import { digitalLinkPath } from "@packagepage/shared";

import { problemOf } from "../api/client.js";
import { api } from "../api/procedures.js";
import { LinkManagement } from "./LinkManagement.js";
import { STATUS_LABEL, STATUS_PILL, displayName } from "./product-row.js";
import { recallAdvisory } from "./recall-advisory.js";
import { LiveRecallBanner } from "./RecallBanner.js";
import {
  checkRetireChallenge,
  retireConsequence,
  retireValidationState,
} from "./retire-challenge.js";

// See ProductList's note: consumers import the stylesheets they use, rather than relying on
// the shell having imported them.
import "@packagepage/ui/input.css";
import "@packagepage/ui/button.css";
import "@packagepage/ui/pill.css";
import "./product-detail.css";

const TABS = ["Details", "Variants", "Batches", "Media", "Links"] as const;
type Tab = (typeof TABS)[number];

export function ProductDetail({ productId }: { productId: string }): ReactNode {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [tab, setTab] = useState<Tab>("Details");
  const [retiring, setRetiring] = useState(false);

  const product = useQuery({
    queryKey: ["products", "read", productId],
    queryFn: () => api.product(productId),
  });
  const org = useQuery({
    queryKey: ["orgs", "current"],
    queryFn: () => api.currentOrg(),
  });
  const recalls = useQuery({ queryKey: ["recalls"], queryFn: () => api.recalls() });
  const variants = useQuery({
    queryKey: ["variants", productId],
    queryFn: () => api.variants(productId),
  });
  const batches = useQuery({
    queryKey: ["batches", productId],
    queryFn: () => api.batches(productId),
  });
  const media = useQuery({
    queryKey: ["media", productId],
    queryFn: () => api.media(productId),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["products"] });
    void queryClient.invalidateQueries({ queryKey: ["recalls"] });
  };

  const publish = useMutation({
    mutationFn: () => api.publishProduct(productId),
    onSuccess: invalidate,
  });

  const retire = useMutation({
    mutationFn: (to: "archived" | "tombstoned") => api.retireProduct(productId, to),
    onSuccess: () => {
      setRetiring(false);
      invalidate();
    },
  });

  const advisory = useMemo(() => {
    if (product.data === undefined) return null;
    return recallAdvisory({
      productId,
      productStatus: product.data.status,
      publishedAt: product.data.published_at,
      gtin14: product.data.gtin14,
      recalls: recalls.data?.recalls ?? [],
    });
  }, [product.data, recalls.data, productId]);

  if (product.isPending) {
    return (
      <Surface>
        <p data-screen-label="Product detail">Loading this product&hellip;</p>
      </Surface>
    );
  }

  if (product.error !== null) {
    const problem = problemOf(product.error);
    return (
      <Surface tone="error" emphasis="filled">
        <div data-screen-label="Product detail">
          <strong>
            {problem === null ? "We could not load this product" : problem.title}
          </strong>
          <p>
            {problem === null
              ? "Its printed codes are unaffected — the resolver answers them from its own mirror and does not depend on this screen."
              : problem.detail}
          </p>
          <p>
            <Link to="/products">Back to the catalogue</Link>
          </p>
        </div>
      </Surface>
    );
  }

  const row = product.data;
  const name = displayName(row.name, row.gtin14);
  const liveUrl =
    org.data === undefined
      ? null
      : `${org.data.resolverBaseUrl}${digitalLinkPath(row.gtin14)}`;
  const terminal = row.status === "tombstoned";
  const ownRecalls = (recalls.data?.recalls ?? []).filter(
    (r) => r.productId === productId,
  );
  const liveRecall = ownRecalls.find((r) => r.status === "active");
  const liftedRecalls = ownRecalls.filter((r) => r.status !== "active");

  return (
    <div data-screen-label="Product detail" className="pp-detail">
      {/* The persistent header. It stays put across tabs — §4 calls it persistent because
          the identity of the thing being edited must never scroll away from the edit. */}
      <Surface>
        <div className="pp-detail__header">
          <div className="pp-detail__identity">
            <div className="pp-detail__name" title={name.text}>
              {name.text}
              {name.lang === null ? null : (
                <span className="pp-detail__lang"> · {name.lang}</span>
              )}
            </div>
            <div className="pp-detail__meta">
              <span className="pp-detail__gtin">{row.gtin14}</span>
              <Button
                variant="quiet"
                onClick={() => void navigator.clipboard?.writeText(row.gtin14)}
              >
                Copy
              </Button>
              <Pill family="product" value={STATUS_PILL[row.status]}>
                {STATUS_LABEL[row.status]}
              </Pill>
              <span className="pp-detail__brand">{row.brand_name}</span>
            </div>
          </div>

          <div className="pp-detail__actions">
            {/* §4 wires this to c35's simulator, and the simulator lives inside the Links
                tab rather than on a screen of its own — so the action opens the tab. A
                separate route would put the answer on one screen and the explanation on
                another, which is the split the acceptance question cannot survive. */}
            <Button variant="secondary" onClick={() => setTab("Links")}>
              Preview a scan
            </Button>
            {/* Honest because it is the same URI a scan requests — not a preview of one. */}
            {liveUrl === null ? null : (
              <a
                className="pp-detail__live"
                href={liveUrl}
                target="_blank"
                rel="noreferrer"
              >
                View live page
              </a>
            )}
            {/*
              §4's third action. A `Link` rather than a `Button`: the generator is a route,
              so this is navigation, and navigation dressed as a button loses middle-click,
              open-in-new-tab and the address a person can paste into a ticket.
            */}
            <Link
              className="pp-detail__live"
              to="/products/$productId/artwork"
              params={{ productId }}
            >
              Download print files
            </Link>
            <Link
              className="pp-detail__live"
              to="/recalls/new"
              search={{ product: productId }}
              aria-label={`Publish a recall notice for ${name.text}`}
            >
              Publish a recall notice
            </Link>
          </div>
        </div>
      </Surface>

      {liveRecall === undefined ? null : (
        <LiveRecallBanner recall={liveRecall} label="Recall live on this product">
          <strong>A recall notice is live on this product</strong>
          <p>
            Scans of{" "}
            {liveRecall.scope === "gtin"
              ? "every unit of this product"
              : liveRecall.lots.join(", ")}{" "}
            land on the notice until it is lifted.
          </p>
          <Link to="/recalls/$recallId" params={{ recallId: liveRecall.id }}>
            Open the recall
          </Link>
        </LiveRecallBanner>
      )}

      {/*
        F3's advisory, and the remedy is a button on this screen rather than a sentence
        about one. The ruling is explicit: the copy says what printed codes are doing right
        now, and *"the remedy it names must be a path the same screen offers."*
      */}
      {advisory === null ? null : (
        <Surface tone="warn" emphasis="filled">
          <div data-screen-label="Recall not resolving">
            <strong>{advisory.title}</strong>
            <p>{advisory.detail}</p>
            {/*
              The remedy only renders where it **is** one. A retired product cannot be
              published — `TRANSITIONS` makes `tombstoned` terminal — so the button would be
              refused by the API, and a control that names a remedy the server declines is
              the same failure as one naming a feature nobody built.
            */}
            {advisory.publishFixes ? (
              <Button
                variant="primary"
                loading={publish.isPending}
                onClick={() => publish.mutate()}
              >
                {publish.isPending ? "Publishing…" : "Publish the product"}
              </Button>
            ) : null}
          </div>
        </Surface>
      )}

      {publish.error === null ? null : (
        <Surface tone="error" emphasis="filled">
          <p>
            {problemOf(publish.error)?.detail ?? "That product could not be published."}
          </p>
        </Surface>
      )}

      <nav className="pp-detail__tabs" aria-label="Product sections">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            className="pp-detail__tab"
            data-active={t === tab ? "true" : undefined}
            aria-current={t === tab ? "page" : undefined}
            onClick={() => setTab(t)}
          >
            {t}
            <span className="pp-detail__tab-count">
              {t === "Variants"
                ? (variants.data?.variants.length ?? "")
                : t === "Batches"
                  ? (batches.data?.batches.length ?? "")
                  : t === "Media"
                    ? (media.data?.media.length ?? "")
                    : ""}
            </span>
          </button>
        ))}
      </nav>

      {tab === "Details" ? (
        <DetailsTab
          status={row.status}
          liveUrl={liveUrl}
          publishing={publish.isPending}
          onPublish={() => publish.mutate()}
        />
      ) : null}

      {tab === "Variants" ? (
        <TabList
          empty="No variants. A variant is a consumer product variant — a different pack size or flavour sharing this code's identity."
          rows={(variants.data?.variants ?? []).map((v) => ({
            key: v.id,
            left: v.cpv,
            right: displayName(v.name, v.cpv).text,
          }))}
        />
      ) : null}

      {tab === "Batches" ? (
        <TabList
          empty="No batches. A batch is a lot number — scans of a coded unit can then answer with its own expiry."
          rows={(batches.data?.batches ?? []).map((b) => ({
            key: b.id,
            left: b.lot,
            // `YYYY-MM-DD`, fixed — §4. Never a locale-formatted date on a screen read
            // in Hamburg and in London.
            right: b.expiry === null ? "No expiry" : b.expiry.slice(0, 10),
          }))}
        />
      ) : null}

      {tab === "Media" ? (
        <TabList
          empty="No media. Images and documents attached here render on the consumer page."
          rows={(media.data?.media ?? []).map((m) => ({
            key: m.id,
            left: m.kind,
            right: m.mime,
          }))}
        />
      ) : null}

      {/* Links carries its own simulator rather than being a list beside one, because
          `components.md` puts the scan-preview simulator *in* link management: the rows
          explain the answer the simulator states, and separating them would make the
          screen's own acceptance question — "what does a scan of LOT42 resolve to, and
          why?" — span two screens. */}
      {tab === "Links" ? <LinkManagement productId={productId} /> : null}

      {liftedRecalls.length === 0 ? null : (
        <Surface data-screen-label="Lifted recall notices">
          <div className="pp-detail__section-title">Lifted recall notices</div>
          <ul className="pp-detail__list">
            {liftedRecalls.map((r) => (
              <li key={r.id}>
                <span className="pp-detail__list-left">
                  <Link to="/recalls/$recallId" params={{ recallId: r.id }}>
                    {r.scope === "gtin" ? "Whole product" : r.lots.join(", ")}
                  </Link>
                </span>
                <span className="pp-detail__list-right">
                  Lifted {(r.liftedAt ?? r.publishedAt).slice(0, 10)}
                </span>
              </li>
            ))}
          </ul>
        </Surface>
      )}

      {terminal ? null : (
        <Surface>
          <div className="pp-detail__danger">
            <div className="pp-detail__section-title">Take this product out of use</div>
            <p>
              <strong>Archive</strong> hides it from your catalogue and changes nothing
              about what a scan gets. It is reversible.
            </p>
            <div className="pp-detail__danger-actions">
              <Button
                variant="secondary"
                loading={retire.isPending}
                onClick={() => retire.mutate("archived")}
              >
                Archive
              </Button>
              {/* 16px of separation from its safe neighbour — components.md. The class
                  carries it; the two are never adjacent. */}
              <Button variant="destructive" onClick={() => setRetiring(true)}>
                Retire permanently
              </Button>
            </div>
          </div>
        </Surface>
      )}

      {retire.error === null ? null : (
        <Surface tone="error" emphasis="filled">
          <p>{problemOf(retire.error)?.detail ?? "That product could not be retired."}</p>
        </Surface>
      )}

      {retiring ? (
        <RetireDialog
          gtin14={row.gtin14}
          liveUrl={liveUrl}
          running={retire.isPending}
          onCancel={() => setRetiring(false)}
          onArchive={() => {
            setRetiring(false);
            retire.mutate("archived");
          }}
          onConfirm={() => retire.mutate("tombstoned")}
        />
      ) : null}

      <p>
        <Link to="/products" onClick={() => void navigate({ to: "/products" })}>
          Back to the catalogue
        </Link>
      </p>
    </div>
  );
}

/**
 * The Details tab, with the draft's one-step publish **stating the latency**.
 *
 * §4: *"draft with one-step publish stating the latency."* The number is the product's own
 * claim — resolution starts within seconds — and stating it is what makes the button
 * honest: a publish that silently takes five seconds reads as broken, and one that promises
 * "instantly" is wrong the first time an edge is slow.
 */
function DetailsTab({
  status,
  liveUrl,
  publishing,
  onPublish,
}: {
  status: string;
  liveUrl: string | null;
  publishing: boolean;
  onPublish: () => void;
}): ReactNode {
  return (
    <Surface>
      <div className="pp-detail__details">
        {status === "draft" ? (
          <>
            <div className="pp-detail__section-title">This code answers nothing yet</div>
            <p>
              A scan of a draft gets a 404 — honestly, because nothing has been published
              for it. Publishing claims the GTIN and starts resolution within seconds; you
              can keep editing afterwards, and every edit is live in the same few seconds.
            </p>
            <Button variant="primary" loading={publishing} onClick={onPublish}>
              {publishing ? "Publishing…" : "Publish"}
            </Button>
          </>
        ) : status === "published" ? (
          <>
            <div className="pp-detail__section-title">This code is answering</div>
            <p>
              {liveUrl === null
                ? "Scans reach your product page."
                : `Scans of the printed barcode request ${liveUrl} and are answered from the resolver's own mirror — which keeps working even when this dashboard does not.`}
            </p>
          </>
        ) : status === "archived" ? (
          <>
            <div className="pp-detail__section-title">Archived</div>
            <p>
              Archiving is a catalogue decision, not a resolution one: scans get exactly
              what they got before. Publish it again to bring it back into the list.
            </p>
          </>
        ) : (
          <>
            <div className="pp-detail__section-title">No longer available</div>
            <p>
              This code is retired permanently. Scans land on the platform&rsquo;s
              &ldquo;no longer available&rdquo; page — the code never dead-ends, because
              the labels carrying it still exist. Nothing here can be changed.
            </p>
          </>
        )}
      </div>
    </Surface>
  );
}

function TabList({
  rows,
  empty,
}: {
  rows: readonly { key: string; left: string; right: string }[];
  empty: string;
}): ReactNode {
  return (
    <Surface>
      {rows.length === 0 ? (
        <p className="pp-detail__empty">{empty}</p>
      ) : (
        <ul className="pp-detail__list">
          {rows.map((r) => (
            <li key={r.key}>
              <span className="pp-detail__list-left">{r.left}</span>
              <span className="pp-detail__list-right">{r.right}</span>
            </li>
          ))}
        </ul>
      )}
    </Surface>
  );
}

/**
 * The typed-confirmation dialog — the one irreversible act in the product.
 *
 * Five rules from `components.md`, each visible in the markup below:
 *
 *   1. **Leads with the physical fact**, not the software one. The labels already exist;
 *      what changes is what they answer.
 *   2. **Names the exact page** a scan gets afterwards.
 *   3. **The typed challenge is the GTIN**, not the word "delete" — typing the number is
 *      what forces the user to read the identifier of the thing they are destroying.
 *   4. **The confirm button is grey until valid and is never the loudest thing here.** The
 *      warning is.
 *   5. **Archive is offered at equal weight**, because it is what the user almost certainly
 *      meant.
 *
 * "Tombstone" and "delete" appear nowhere a user can see. The word for the state is *No
 * longer available* and the word for the act is *Retire permanently* — the shopper's
 * vocabulary on both audiences' screens.
 */
export function RetireDialog({
  gtin14,
  liveUrl,
  running,
  onCancel,
  onArchive,
  onConfirm,
}: {
  gtin14: string;
  liveUrl: string | null;
  running: boolean;
  onCancel: () => void;
  onArchive: () => void;
  onConfirm: () => void;
}): ReactNode {
  const [typed, setTyped] = useState("");
  const challenge = checkRetireChallenge(gtin14, typed);

  // The mapping lives in `retire-challenge.ts` as a pure function rather than inline here.
  // This component only mounts behind an `onClick`, which `renderToStaticMarkup` can never
  // fire and jsdom is not in this suite — so anything expressed here is reachable only by
  // a hand-copied reconstruction in the test, which is a copy that can drift silently.
  // `retireValidationState` is callable with any challenge and needs no interactivity.
  const validation = retireValidationState(challenge);

  return (
    <div
      className="pp-detail__dialog"
      role="dialog"
      aria-modal="true"
      aria-label="Retire permanently"
      data-screen-label="Retire permanently"
    >
      <Surface tone="warn" emphasis="filled">
        <div className="pp-detail__dialog-body">
          <div className="pp-detail__section-title">Retire {gtin14} permanently</div>

          <p>
            Every label already printed with this code stays in the world, and every one
            of them keeps being scanned. This does not stop that — nothing can.
          </p>
          <p>{retireConsequence(liveUrl ?? `the code ${gtin14}`)}</p>

          {/*
            **No `onFix`, and its absence is the point.** `Input`'s one-click fix carries
            the corrected value so the button doubles as the explanation, which is right on
            every other field in the product and wrong on exactly this one: a button that
            fills in the challenge types the confirmation for the user and destroys the only
            thing the challenge does — making them read the identifier of the thing they are
            destroying. The offending character is still marked in place, because *finding*
            the typo is help and *fixing* it is not.
          */}
          <Input
            label="Type this product's code to confirm"
            value={typed}
            mono
            onChange={(e) => setTyped(e.target.value)}
            {...(validation === undefined ? {} : { validation })}
          />

          <div className="pp-detail__dialog-actions">
            {/* Equal weight, and it is what they almost certainly meant. */}
            <Button variant="secondary" onClick={onArchive}>
              Archive instead
            </Button>
            <Button variant="quiet" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!challenge.satisfied}
              loading={running}
              onClick={onConfirm}
            >
              {running ? "Retiring…" : "Retire permanently"}
            </Button>
          </div>
        </div>
      </Surface>
    </div>
  );
}
